if (!customElements.get('sense-mini-gallery')) {
  class SenseMiniGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-sense-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-sense-gallery-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-sense-gallery-dot]'));
      if (!this.track || this.slides.length < 2 || this.dots.length !== this.slides.length) return;

      this.initialized = true;
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('click', this.onClick.bind(this), listenerOptions);
      this.addEventListener('keydown', this.onKeydown.bind(this), listenerOptions);
      this.addEventListener('touchstart', this.onTouchStart.bind(this), { ...listenerOptions, passive: true });
      this.addEventListener('touchmove', this.onTouchMove.bind(this), { ...listenerOptions, passive: false });
      this.addEventListener('touchend', this.onTouchEnd.bind(this), listenerOptions);
      this.addEventListener('touchcancel', this.resetTouchState.bind(this), listenerOptions);
      this.showSlide(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.initialized = false;
    }

    onClick(event) {
      if (this.suppressClickUntil && Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const arrow = event.target.closest('[data-sense-gallery-direction]');
      if (arrow && this.contains(arrow)) {
        event.preventDefault();
        event.stopPropagation();
        const direction = Number(arrow.dataset.senseGalleryDirection);
        const nextIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
        this.showSlide(nextIndex);
        return;
      }

      const dot = event.target.closest('[data-sense-gallery-dot]');
      if (!dot || !this.contains(dot)) return;

      event.preventDefault();
      event.stopPropagation();
      this.showSlide(Number(dot.dataset.index));
    }

    onKeydown(event) {
      const arrow = event.target.closest('[data-sense-gallery-direction]');
      if (arrow && this.contains(arrow) && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        const direction = Number(arrow.dataset.senseGalleryDirection);
        const nextIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
        this.showSlide(nextIndex);
        return;
      }

      const dot = event.target.closest('[data-sense-gallery-dot]');
      if (!dot || !this.contains(dot)) return;

      const currentIndex = this.dots.indexOf(dot);
      const lastIndex = this.dots.length - 1;
      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.showSlide(nextIndex);
      this.dots[nextIndex]?.focus();
    }

    onTouchStart(event) {
      if (!window.matchMedia('(max-width: 749px)').matches || !event.touches?.length) return;
      if (!event.target.closest('[data-sense-gallery-track]')) return;

      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchIsHorizontal = false;
      this.touchTracking = true;
    }

    onTouchMove(event) {
      if (!this.touchTracking || !event.touches?.length) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      const distanceX = Math.abs(deltaX);
      const distanceY = Math.abs(deltaY);

      if (!this.touchIsHorizontal) {
        if (Math.max(distanceX, distanceY) < 14) return;
        if (distanceX <= distanceY) {
          this.touchTracking = false;
          return;
        }
        this.touchIsHorizontal = true;
      }

      event.preventDefault();
      event.stopPropagation();
    }

    onTouchEnd(event) {
      if (!this.touchTracking || !this.touchIsHorizontal) {
        this.resetTouchState();
        return;
      }

      const touch = event.changedTouches?.[0];
      const deltaX = touch ? touch.clientX - this.touchStartX : 0;
      const direction = deltaX < 0 ? 1 : -1;
      const nextIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
      this.showSlide(nextIndex);
      this.suppressClickUntil = Date.now() + 400;
      this.resetTouchState();
    }

    resetTouchState() {
      this.touchStartX = undefined;
      this.touchStartY = undefined;
      this.touchIsHorizontal = false;
      this.touchTracking = false;
    }

    showSlide(index) {
      const resolvedIndex = Number.isInteger(index) ? index : 0;
      this.currentIndex = Math.max(0, Math.min(resolvedIndex, this.slides.length - 1));
      this.track.style.setProperty('--sense-mini-gallery-offset', `${this.currentIndex * -100}%`);

      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', String(slideIndex !== this.currentIndex));
      });

      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === this.currentIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('sense-mini-gallery', SenseMiniGallery);
}

if (!customElements.get('sense-modal-gallery')) {
  class SenseModalGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.slides = Array.from(this.querySelectorAll('[data-sense-modal-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-sense-modal-gallery-thumbnail]'));
      if (!this.slides.length || this.thumbnails.length !== this.slides.length) return;

      this.initialized = true;
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('click', this.onClick.bind(this), listenerOptions);
      this.addEventListener('keydown', this.onKeydown.bind(this), listenerOptions);
      this.showSlide(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.initialized = false;
    }

    onClick(event) {
      const thumbnail = event.target.closest('[data-sense-modal-gallery-thumbnail]');
      if (!thumbnail || !this.contains(thumbnail)) return;

      event.preventDefault();
      this.showSlide(Number(thumbnail.dataset.index));
    }

    onKeydown(event) {
      const thumbnail = event.target.closest('[data-sense-modal-gallery-thumbnail]');
      if (!thumbnail || !this.contains(thumbnail)) return;

      const currentIndex = this.thumbnails.indexOf(thumbnail);
      const lastIndex = this.thumbnails.length - 1;
      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.showSlide(nextIndex);
      this.thumbnails[nextIndex]?.focus();
    }

    showSlide(index) {
      const resolvedIndex = Number.isInteger(index) ? index : 0;
      const currentIndex = Math.max(0, Math.min(resolvedIndex, this.slides.length - 1));

      this.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === currentIndex;
        slide.hidden = !isCurrent;
        slide.setAttribute('aria-hidden', String(!isCurrent));
      });

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        if (thumbnailIndex === currentIndex) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('sense-modal-gallery', SenseModalGallery);
}

if (!customElements.get('sense-carousel-progress')) {
  class SenseCarouselProgress extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.slider = document.getElementById(this.dataset.sliderId);
      this.thumb = this.querySelector('.sense-complementary-products__progress-thumb');
      if (!this.slider || !this.thumb) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.onScroll = this.scheduleUpdate.bind(this);
      this.onContentLoad = this.scheduleUpdate.bind(this);
      this.slider.addEventListener('scroll', this.onScroll, {
        passive: true,
        signal: this.abortController.signal,
      });
      this.slider.addEventListener('load', this.onContentLoad, {
        capture: true,
        signal: this.abortController.signal,
      });

      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
      this.resizeObserver.observe(this.slider);
      this.update();
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame !== undefined) window.cancelAnimationFrame(this.frame);
      this.abortController = undefined;
      this.resizeObserver = undefined;
      this.onContentLoad = undefined;
      this.frame = undefined;
      this.initialized = false;
    }

    scheduleUpdate() {
      if (!this.initialized || this.frame !== undefined) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = undefined;
        if (!this.initialized) return;
        this.update();
      });
    }

    update() {
      const maximumScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const hasOverflow = maximumScroll > 1;
      this.hidden = !hasOverflow;
      if (!hasOverflow || !this.clientWidth) return;

      const trackWidth = this.clientWidth;
      const minimumThumbWidth = Math.min(24, trackWidth);
      const proportionalWidth = (this.slider.clientWidth / this.slider.scrollWidth) * trackWidth;
      const thumbWidth = Math.min(trackWidth, Math.max(minimumThumbWidth, proportionalWidth));
      const scrollLeft = Math.max(0, Math.min(this.slider.scrollLeft, maximumScroll));
      const progress = scrollLeft / maximumScroll;
      const thumbOffset = progress * (trackWidth - thumbWidth);

      this.style.setProperty('--sense-carousel-progress-thumb-width', `${thumbWidth}px`);
      this.style.setProperty('--sense-carousel-progress-thumb-offset', `${thumbOffset}px`);
    }
  }

  customElements.define('sense-carousel-progress', SenseCarouselProgress);
}
